import { useState } from "react";

import { MathJax, MathJaxContext } from "better-react-mathjax";

import { ArteryScene } from "./ArteryScene";
import { FlowProfileCanvas } from "./FlowProfileCanvas";

const BASE_DIAMETER_MM = 3.0;      // typical coronary artery
const BASE_FLOW_ML_S = 80;         // resting coronary flow
const VISCOSITY_PA_S = 0.0035;     // blood viscosity
const SEGMENT_LENGTH_M = 0.02;     // 2 cm segment

function CoronaryFlows() {
  const [stenosis, setStenosis] = useState(0.3);

  const diameterMm = BASE_DIAMETER_MM * (1 - 0.6 * stenosis);
  const diameterM = diameterMm / 1000;

  const flowMlPerS = BASE_FLOW_ML_S * Math.pow(diameterMm / BASE_DIAMETER_MM, 4);
  const flowM3PerS = flowMlPerS / 1e6;

  const velocityMPerS = flowM3PerS / (Math.PI * Math.pow(diameterM / 2, 2));

  const pressureDropPa = (8 * VISCOSITY_PA_S * SEGMENT_LENGTH_M * flowM3PerS) / (Math.PI * Math.pow(diameterM, 4));

  return (
    <div className="body-content bg-fff pt-70l-110m-50s pb-100">

      <div className="panel large pt-20l-10s">
        <div className="grid-x">

          <div className="large-12 medium-12 small-12 cell text-left">
            <div className="font-source-sans font-size-20 font-weight-800 txt-dark-blue bb1-dark-blue uppercase pb-5">Coronary Flow Simulation</div>
          </div>

        </div>
      </div>

      <div className="panel large pt-20l-10s">
        <div className="grid-x">

          <div className="large-12 medium-12 small-12 cell text-center plr-5 pb-20">
            <div style={{ width: "100%", height: "auto" }}>
              <div className="font-source-sans font-size-20 font-weight-800 txt-000 pb-5">Flow Profile</div>
              <FlowProfileCanvas stenosis={stenosis} />
              <div></div>
              <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                <label htmlFor="stenosis-slider">
                  Stenosis: {Math.round(stenosis * 100)}%
                </label>
                <input
                  type="range"
                  id="stenosis-slider"
                  min={0}
                  max={1}
                  step={0.01}
                  value={stenosis}
                  onChange={(e) => setStenosis(Number(e.target.value))}
                />
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="large-12 medium-12 small-12 cell b1-ccc text-center pt-10 plr-5">
            <div style={{ width: "100%", height: "auto" }}>
              <div className="font-source-sans font-size-20 font-weight-800 txt-000 pb-5">Artery Scene</div>
              <ArteryScene
                stenosis={stenosis}
                diameterMm={diameterMm}
                flowMlPerS={flowMlPerS}
                velocityMPerS={velocityMPerS}
                pressureDropPa={pressureDropPa}
              />
            </div>
          </div>

        </div>

        <div className="large-12 medium-12 small-12 cell text-center">
          <div className="font-source-sans font-size-24 font-weight-800 txt-000 uppercase pt-10">
            Coronary Flow <div className="pt-5 show-for-small-only"></div>Simulation Model
          </div>
          <MathJaxContext>
            {/* Stenosis */}
            <div className="font-source-sans font-size-20 font-weight-600 txt-000 pt-30">
              1. Percent Stenosis
            </div>
            <div className="font-source-sans font-size-18 font-weight-400 txt-000 ptb-5">
              The fraction of artery diameter reduction due to plaque:
            </div>
            <div className="formula mt-10">
              <MathJax>{`\\( S = \\frac{D_{\\text{normal}} - D_{\\text{stenosed}}}{D_{\\text{normal}}} \\times 100\\% \\)`}</MathJax>
            </div>

            {/* Artery Diameter */}
            <div className="font-source-sans font-size-20 font-weight-600 txt-000 pt-30">
              2. Artery Diameter
            </div>
            <div className="font-source-sans font-size-18 font-weight-400 txt-000 ptb-5">
              The remaining diameter as stenosis changes:
            </div>
            <div className="formula mt-10">
              <MathJax>{`\\( D_{\\text{stenosed}} = D_{\\text{normal}} \\times (1 - S) \\)`}</MathJax>
            </div>

            {/* Flow Rate */}
            <div className="font-source-sans font-size-20 font-weight-600 txt-000 pt-30">
              3. Coronary Flow Rate (Simplified Poiseuille)
            </div>
            <div className="font-source-sans font-size-18 font-weight-400 txt-000 ptb-5">
              Assuming laminar flow, flow rate is proportional to the fourth power of radius:
            </div>
            <div className="formula mt-10">
              <MathJax>{`\\( Q = Q_{0} \\times \\left( \\frac{D_{\\text{stenosed}}}{D_{\\text{normal}}} \\right)^4 \\)`}</MathJax>
            </div>

            {/* Velocity */}
            <div className="font-source-sans font-size-20 font-weight-600 txt-000 pt-30">
              4. Blood Flow Velocity
            </div>
            <div className="font-source-sans font-size-18 font-weight-400 txt-000 ptb-5">
              Velocity in the stenosed segment:
            </div>
            <div className="formula mt-10">
              <MathJax>{`\\( v = \\frac{Q}{A} = \\frac{4 Q}{\\pi D_{\\text{stenosed}}^2} \\)`}</MathJax>
            </div>

            {/* Pressure Drop */}
            <div className="font-source-sans font-size-20 font-weight-600 txt-000 pt-30">
              5. Pressure Drop Across Stenosis
            </div>
            <div className="font-source-sans font-size-18 font-weight-400 txt-000 ptb-5">
              A simplified pressure drop using Poiseuille’s law:
            </div>
            <div className="formula mt-10">
              <MathJax>{`\\( \\Delta P = \\frac{8 \\mu L Q}{\\pi D_{\\text{stenosed}}^4} \\)`}</MathJax>
            </div>
          </MathJaxContext>
        </div>
      </div>
    </div >
  );
}

export default CoronaryFlows;